import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Empty,
  message,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import moment from 'moment';
import { DownloadOutlined } from '@ant-design/icons';
import {
  consultaConfirmaCancelacion,
  descargarAcuseCancelacion,
  getConsultasEstadoCancelacion,
  getSolicitudesCancelacion,
  nombreArchivoAcuse,
  presentacionSolicitudCancelacion,
} from 'api/nominas/timbrado';
import { DATE_FRONTEND_FORMAT } from 'utils/normalizers';

const { Title } = Typography;

const SIN_DATO = '—';
const FORMATO_FECHA_HORA = `${DATE_FRONTEND_FORMAT} HH:mm:ss`;
export const TEXTO_PENDIENTE_DE_CONFIRMAR = 'Pendiente de Confirmar';
export const TEXTO_CONFIRMADA_POR_CONSULTA = 'Confirmada por Consulta';
export const TEXTO_CONSULTA_CONFIRMA_CANCELACION = 'Confirma la Cancelación del CFDI';
export const TEXTO_RECIBO_ACTUALIZADO = 'Pasó el Recibo a Cancelado';

const formatearFechaHora = (valor) => {
  if (!valor) return SIN_DATO;
  const fecha = moment(valor);
  return fecha.isValid() ? fecha.format(FORMATO_FECHA_HORA) : SIN_DATO;
};

// Solo texto: React escapa el contenido; nunca interpretarlo como HTML
const texto = (valor) => (valor === null || valor === undefined || valor === '' ? SIN_DATO : valor);

// Guarda el acuse tal como llegó del servidor, sin reconstruir su contenido
const guardarArchivo = (blob, nombre) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombre;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Consulta un historial del recibo al montarse; descarta respuestas de otro recibo
const useHistorialDelRecibo = (consultar, reciboId, activo) => {
  const [loading, setLoading] = useState(false);
  const [historial, setHistorial] = useState(null);

  useEffect(() => {
    let mounted = true;
    if (!activo || !reciboId) {
      setHistorial(null);
      return undefined;
    }
    const fetchHistorial = async () => {
      setLoading(true);
      const respuesta = await consultar(reciboId);
      if (mounted) {
        setHistorial({ ...respuesta, reciboId });
        setLoading(false);
      }
    };
    fetchHistorial();
    return () => { mounted = false; };
  }, [consultar, reciboId, activo]);

  return { loading, historial: historial?.reciboId === reciboId ? historial : null };
};

const Seccion = ({
  titulo, loading, historial, textos, columnas,
}) => {
  const contenido = () => {
    const {
      registros, total, error, sinPermiso,
    } = historial;
    if (sinPermiso) return <Alert type="warning" showIcon message={textos.sinPermiso} />;
    if (error) return <Alert type="error" showIcon message={textos.error} />;
    if (!registros.length) return <Empty description={textos.vacio} />;
    return (
      <>
        {total > registros.length && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Se muestran ${registros.length} de ${total} ${textos.unidad}.`}
          />
        )}
        <Table
          rowKey="id"
          size="small"
          columns={columnas}
          dataSource={registros}
          pagination={false}
          scroll={{ x: 1100 }}
        />
      </>
    );
  };

  return (
    <section style={{ marginBottom: 24 }}>
      <Title level={5}>{titulo}</Title>
      <Spin tip="Cargando..." spinning={loading}>
        {!loading && historial ? contenido() : <div style={{ minHeight: 80 }} />}
      </Spin>
    </section>
  );
};

Seccion.propTypes = {
  titulo: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  historial: PropTypes.shape({
    registros: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })),
    total: PropTypes.number,
    error: PropTypes.bool,
    sinPermiso: PropTypes.bool,
  }),
  textos: PropTypes.shape({
    sinPermiso: PropTypes.string,
    error: PropTypes.string,
    vacio: PropTypes.string,
    unidad: PropTypes.string,
  }).isRequired,
  columnas: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string })).isRequired,
};

Seccion.defaultProps = {
  historial: null,
};

const columnasConsultas = [
  {
    title: 'Fecha de Consulta',
    dataIndex: 'fechaConsulta',
    key: 'fechaConsulta',
    render: formatearFechaHora,
  },
  {
    title: 'Resultado',
    dataIndex: 'resultado',
    key: 'resultado',
    render: (valor, consulta) => (
      <>
        {texto(valor)}
        {consultaConfirmaCancelacion(consulta) && (
          <Tag color="green" style={{ marginLeft: 6 }}>{TEXTO_CONSULTA_CONFIRMA_CANCELACION}</Tag>
        )}
        {consulta.reciboActualizado && (
          <Tag color="blue" style={{ marginLeft: 6 }}>{TEXTO_RECIBO_ACTUALIZADO}</Tag>
        )}
      </>
    ),
  },
  {
    title: 'Estado', dataIndex: 'estadoSat', key: 'estadoSat', render: texto,
  },
  {
    title: 'EsCancelable', dataIndex: 'esCancelable', key: 'esCancelable', render: texto,
  },
  {
    title: 'EstatusCancelacion', dataIndex: 'estatusCancelacion', key: 'estatusCancelacion', render: texto,
  },
  {
    title: 'Código', dataIndex: 'codigo', key: 'codigo', render: texto,
  },
  {
    title: 'Descripción', dataIndex: 'descripcion', key: 'descripcion', render: texto,
  },
  {
    title: 'Usuario', dataIndex: 'usuario', key: 'usuario', render: texto,
  },
];

const CancelacionesTimbrado = ({ reciboId, verSolicitudes, verConsultas }) => {
  const solicitudes = useHistorialDelRecibo(getSolicitudesCancelacion, reciboId, verSolicitudes);
  const consultas = useHistorialDelRecibo(getConsultasEstadoCancelacion, reciboId, verConsultas);
  const [descargando, setDescargando] = useState(null);
  // Descarga en curso: se aborta si la pestaña se cierra o se abandona antes de terminar,
  // para que no se guarde un archivo sin vista abierta ni quede una petición huérfana
  const descargaEnCurso = useRef(null);

  useEffect(() => () => {
    descargaEnCurso.current?.abort();
    descargaEnCurso.current = null;
  }, []);

  const descargar = async (solicitud) => {
    if (descargaEnCurso.current) return;
    const controlador = new AbortController();
    descargaEnCurso.current = controlador;
    setDescargando(solicitud.id);
    const {
      acuse, detalle, cancelada,
    } = await descargarAcuseCancelacion(solicitud.id, controlador.signal);
    if (cancelada || controlador.signal.aborted) return;
    descargaEnCurso.current = null;
    setDescargando(null);
    if (acuse) {
      guardarArchivo(acuse, nombreArchivoAcuse(solicitud.uuid, solicitud.id));
    } else if (detalle) {
      message.warning(detalle);
    } else {
      message.error('No fue posible descargar el acuse de cancelación.');
    }
  };

  const columnasSolicitudes = [
    {
      title: 'Fecha de Solicitud',
      dataIndex: 'fechaSolicitud',
      key: 'fechaSolicitud',
      render: formatearFechaHora,
    },
    {
      title: 'Fecha de Respuesta',
      dataIndex: 'fechaRespuesta',
      key: 'fechaRespuesta',
      render: formatearFechaHora,
    },
    {
      title: 'Motivo', dataIndex: 'motivo', key: 'motivo', render: texto,
    },
    {
      title: 'Folio de Sustitución', dataIndex: 'folioSustitucion', key: 'folioSustitucion', render: texto,
    },
    {
      title: 'Estado',
      dataIndex: 'estadoDescripcion',
      key: 'estadoDescripcion',
      // `estadoDescripcion` es el estado de la solicitud; la etiqueta, lo que la última
      // consulta guardada en ella dice del CFDI
      render: (valor, solicitud) => {
        const presentacion = presentacionSolicitudCancelacion(solicitud);
        return (
          <>
            {texto(valor)}
            {presentacion === 'pendiente' && (
              <Tag color="orange" style={{ marginLeft: 6 }}>{TEXTO_PENDIENTE_DE_CONFIRMAR}</Tag>
            )}
            {presentacion === 'confirmada' && (
              <Tag color="green" style={{ marginLeft: 6 }}>{TEXTO_CONFIRMADA_POR_CONSULTA}</Tag>
            )}
          </>
        );
      },
    },
    {
      title: 'Código', dataIndex: 'codigo', key: 'codigo', render: texto,
    },
    {
      title: 'EstatusUUID', dataIndex: 'estatusUuid', key: 'estatusUuid', render: texto,
    },
    {
      title: 'Descripción', dataIndex: 'descripcion', key: 'descripcion', render: texto,
    },
    {
      title: 'Última Consulta de Estado',
      key: 'ultimaConsulta',
      render: (_, solicitud) => (solicitud.fechaConsultaEstado
        ? `${texto(solicitud.estadoSat)} · ${texto(solicitud.esCancelable)} · `
          + `${texto(solicitud.estatusCancelacion)} · ${formatearFechaHora(solicitud.fechaConsultaEstado)}`
        : SIN_DATO),
    },
    {
      title: 'Usuario', dataIndex: 'usuario', key: 'usuario', render: texto,
    },
    {
      title: 'Acuse',
      key: 'acuse',
      fixed: 'right',
      render: (_, solicitud) => {
        const otraEnCurso = descargando !== null && descargando !== solicitud.id;
        const boton = (
          <Button
            size="small"
            icon={<DownloadOutlined />}
            disabled={!solicitud.tieneAcuse || otraEnCurso}
            loading={descargando === solicitud.id}
            onClick={() => descargar(solicitud)}
          >
            Descargar Acuse
          </Button>
        );
        if (solicitud.tieneAcuse) return boton;
        return (
          <Tooltip
            title={`La solicitud no tiene acuse (estado: ${texto(solicitud.estadoDescripcion)}).`}
          >
            <span>{boton}</span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      {verSolicitudes && (
        <Seccion
          titulo="Solicitudes"
          loading={solicitudes.loading}
          historial={solicitudes.historial}
          columnas={columnasSolicitudes}
          textos={{
            sinPermiso: 'No tienes permiso para consultar las solicitudes de cancelación.',
            error: 'No fue posible consultar las solicitudes de cancelación.',
            vacio: 'Este recibo no tiene solicitudes de cancelación SystiCloud',
            unidad: 'solicitudes',
          }}
        />
      )}
      {verConsultas && (
        <Seccion
          titulo="Consultas de Estado"
          loading={consultas.loading}
          historial={consultas.historial}
          columnas={columnasConsultas}
          textos={{
            sinPermiso: 'No tienes permiso para consultar las consultas de estado de cancelación.',
            error: 'No fue posible consultar las consultas de estado de cancelación.',
            vacio: 'Este recibo no tiene consultas de estado de cancelación',
            unidad: 'consultas',
          }}
        />
      )}
    </>
  );
};

CancelacionesTimbrado.propTypes = {
  reciboId: PropTypes.number,
  verSolicitudes: PropTypes.bool,
  verConsultas: PropTypes.bool,
};

CancelacionesTimbrado.defaultProps = {
  reciboId: null,
  verSolicitudes: false,
  verConsultas: false,
};

export default CancelacionesTimbrado;
